import React, { useMemo } from 'react';
import {
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiLoader,
  FiCloud,
  FiAlertTriangle,
  FiCheck,
  FiUpload,
  FiPlay,
  FiDatabase,
  FiSave,
} from 'react-icons/fi';
import { IconType } from 'react-icons';

interface BedrockStatusConfig {
  label: string;
  description: string;
  color: string;
  icon: IconType;
  animate: boolean;
}

const BEDROCK_STATUS_CONFIG: Record<string, BedrockStatusConfig> = {
  InProgress: {
    label: 'In Progress',
    description: 'Bedrock evaluation is running...',
    color: 'blue',
    icon: FiLoader,
    animate: true,
  },
  Completed: {
    label: 'Completed',
    description: 'Evaluation completed successfully',
    color: 'green',
    icon: FiCheckCircle,
    animate: false,
  },
  Failed: {
    label: 'Failed',
    description: 'Evaluation failed',
    color: 'red',
    icon: FiXCircle,
    animate: false,
  },
  TimedOut: {
    label: 'Timed Out',
    description: 'Evaluation exceeded timeout limit',
    color: 'amber',
    icon: FiAlertTriangle,
    animate: false,
  },
  Stopping: {
    label: 'Stopping',
    description: 'Evaluation is being stopped...',
    color: 'amber',
    icon: FiLoader,
    animate: true,
  },
  Stopped: {
    label: 'Stopped',
    description: 'Evaluation was stopped',
    color: 'gray',
    icon: FiXCircle,
    animate: false,
  },
  pending: {
    label: 'Preparing',
    description: 'Preparing evaluation job...',
    color: 'gray',
    icon: FiClock,
    animate: false,
  },
  creating_job: {
    label: 'Creating Job',
    description: 'Creating Bedrock evaluation job...',
    color: 'blue',
    icon: FiCloud,
    animate: true,
  },
  uploading_dataset: {
    label: 'Uploading',
    description: 'Uploading dataset to S3...',
    color: 'blue',
    icon: FiLoader,
    animate: true,
  },
};

const getElapsedMinutes = (startedAt: string | null | undefined): number => {
  if (!startedAt) return 0;

  let timestamp = startedAt;
  if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
    timestamp = timestamp + 'Z';
  }

  const start = new Date(timestamp);
  const now = new Date();
  const diffMs = Math.max(0, now.getTime() - start.getTime());
  return diffMs / 60000;
};

const formatElapsedTime = (startedAt: string | null | undefined): string | null => {
  if (!startedAt) return null;

  const elapsedMinutes = getElapsedMinutes(startedAt);
  const diffMins = Math.floor(elapsedMinutes);
  const diffSecs = Math.floor((elapsedMinutes % 1) * 60);

  if (diffMins > 0) {
    return `${diffMins}m ${diffSecs}s`;
  }
  return `${diffSecs}s`;
};

interface ProgressEstimate {
  phase: string;
  percent: number;
  elapsedMinutes: number;
  estimatedTotalMinutes: number;
  remainingMinutes: number;
  isEstimate: boolean;
  currentStep: number;
}

const estimateProgress = (
  startedAt: string | null | undefined,
  testCaseCount = 10
): ProgressEstimate | null => {
  if (!startedAt) return null;

  const elapsedMinutes = getElapsedMinutes(startedAt);
  const estimatedEvalMinutes = Math.max(5, testCaseCount * 0.5);
  const estimatedTotalMinutes = 2 + estimatedEvalMinutes + 2;

  let phase: string;
  let progress: number;

  if (elapsedMinutes < 2) {
    phase = 'Setting up...';
    progress = Math.min(10, elapsedMinutes * 5);
  } else if (elapsedMinutes < 2 + estimatedEvalMinutes) {
    phase = 'Evaluating test cases...';
    const evalProgress = (elapsedMinutes - 2) / estimatedEvalMinutes;
    progress = 10 + evalProgress * 80;
  } else {
    phase = 'Finalizing results...';
    progress = Math.min(99, 90 + (elapsedMinutes - 2 - estimatedEvalMinutes) * 5);
  }

  let currentStep = 0;
  if (elapsedMinutes >= 2) currentStep = 1;
  if (elapsedMinutes >= 2 + estimatedEvalMinutes * 0.5) currentStep = 2;
  if (elapsedMinutes >= 2 + estimatedEvalMinutes) currentStep = 3;

  return {
    phase,
    percent: Math.round(progress),
    elapsedMinutes: Math.round(elapsedMinutes * 10) / 10,
    estimatedTotalMinutes: Math.round(estimatedTotalMinutes),
    remainingMinutes: Math.max(0, Math.round(estimatedTotalMinutes - elapsedMinutes)),
    isEstimate: true,
    currentStep,
  };
};

interface ProgressStep {
  label: string;
  icon: IconType;
  description: string;
}

interface ProgressStepsProps {
  currentStep: number;
}

const ProgressSteps: React.FC<ProgressStepsProps> = ({ currentStep }) => {
  const steps: ProgressStep[] = [
    { label: 'Setup', icon: FiUpload, description: 'Preparing data' },
    { label: 'Inference', icon: FiPlay, description: 'Running model' },
    { label: 'Scoring', icon: FiDatabase, description: 'Calculating metrics' },
    { label: 'Saving', icon: FiSave, description: 'Storing results' },
  ];

  return (
    <div className="flex items-center justify-between mb-4">
      {steps.map((step, idx) => {
        const Icon = step.icon;
        const isComplete = idx < currentStep;
        const isCurrent = idx === currentStep;

        let circleClass: string;
        if (isComplete) circleClass = 'bg-green-500 text-white';
        else if (isCurrent) circleClass = 'bg-blue-500 text-white ring-4 ring-blue-200 dark:ring-blue-800';
        else circleClass = 'bg-gray-200 dark:bg-gray-700 text-gray-400';

        const labelClass =
          isComplete || isCurrent
            ? 'text-gray-900 dark:text-white'
            : 'text-gray-400';

        const descClass = isCurrent
          ? 'text-blue-600 dark:text-blue-400'
          : 'text-gray-400 dark:text-gray-500';

        const iconClass = `w-4 h-4${isCurrent ? ' animate-pulse' : ''}`;

        return (
          <React.Fragment key={step.label}>
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${circleClass}`}
              >
                {isComplete
                  ? <FiCheck className="w-4 h-4" />
                  : <Icon className={iconClass} />
                }
              </div>
              <span className={`mt-1 text-xs font-medium ${labelClass}`}>{step.label}</span>
              <span className={`text-xs ${descClass}`}>
                {isCurrent ? step.description : ''}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 ${
                  idx < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

interface BedrockEvaluationProgressProps {
  status?: string;
  bedrockJobStatus?: string;
  bedrockJobArn?: string;
  startedAt?: string | null;
  lastPolledAt?: string | null;
  errorMessage?: string;
  testCaseCount?: number;
  timeoutMinutes?: number;
  compact?: boolean;
}

const BedrockEvaluationProgress: React.FC<BedrockEvaluationProgressProps> = ({
  status,
  bedrockJobStatus,
  bedrockJobArn,
  startedAt,
  lastPolledAt,
  errorMessage,
  testCaseCount = 10,
  timeoutMinutes = 60,
  compact = false,
}) => {
  const displayStatus = bedrockJobStatus ?? status ?? 'pending';
  const config = BEDROCK_STATUS_CONFIG[displayStatus] ?? BEDROCK_STATUS_CONFIG.pending;
  const Icon = config.icon;

  const elapsed = formatElapsedTime(startedAt);
  const isRunning =
    displayStatus === 'InProgress' ||
    displayStatus === 'creating_job' ||
    displayStatus === 'uploading_dataset' ||
    displayStatus === 'Stopping';

  const progress = useMemo(() => {
    if (!startedAt || !isRunning) return null;
    return estimateProgress(startedAt, testCaseCount);
  }, [startedAt, testCaseCount, isRunning]);

  const timeoutWarning = useMemo((): string | null => {
    if (!startedAt || !timeoutMinutes || !isRunning) return null;
    const elapsedMinutes = getElapsedMinutes(startedAt);
    const remaining = timeoutMinutes - elapsedMinutes;

    if (remaining < 10 && remaining > 0) {
      return `Warning: Job will timeout in ${Math.round(remaining)} minutes`;
    }
    if (remaining <= 0) {
      return 'Job has exceeded timeout and may be marked as failed soon';
    }
    return null;
  }, [startedAt, timeoutMinutes, isRunning]);

  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-${config.color}-600 dark:text-${config.color}-400`}
      >
        <Icon className={`w-4 h-4 ${config.animate ? 'animate-spin' : ''}`} />
        <span>{config.label}</span>
        {elapsed && isRunning && (
          <span className="text-gray-500 dark:text-gray-400">({elapsed})</span>
        )}
        {progress && isRunning && (
          <span className="text-gray-400 dark:text-gray-500">~{progress.percent}%</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`p-4 rounded-lg border bg-${config.color}-50 dark:bg-${config.color}-900/20 border-${config.color}-200 dark:border-${config.color}-700`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full bg-${config.color}-100 dark:bg-${config.color}-800`}>
          <Icon
            className={`w-5 h-5 text-${config.color}-600 dark:text-${config.color}-400 ${
              config.animate ? 'animate-spin' : ''
            }`}
          />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h4 className={`font-medium text-${config.color}-800 dark:text-${config.color}-200`}>
              {config.label}
            </h4>
            {elapsed && isRunning && (
              <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <FiClock className="w-3 h-3" />
                {elapsed}
              </span>
            )}
          </div>

          <p className={`text-sm text-${config.color}-700 dark:text-${config.color}-300 mt-1`}>
            {config.description}
          </p>

          {isRunning && progress && (
            <div className="mt-4">
              <ProgressSteps currentStep={progress.currentStep} />
            </div>
          )}

          {isRunning && progress && (
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600 dark:text-gray-400">{progress.phase}</span>
                <span className="text-gray-400 dark:text-gray-500">
                  ~{progress.percent}% (estimate)
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                <div
                  className={`bg-${config.color}-500 h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                ~{progress.remainingMinutes} min remaining (estimated)
              </p>
            </div>
          )}

          {timeoutWarning && (
            <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <FiAlertTriangle className="w-3 h-3 flex-shrink-0" />
              {timeoutWarning}
            </div>
          )}

          {isRunning && (
            <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <FiCheck className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium">Safe to navigate away</span>
              </div>
              <p className="text-xs text-green-600 dark:text-green-500 mt-1 ml-6">
                Your evaluation will continue running. Check back to see results.
              </p>
            </div>
          )}

          {errorMessage && (['Failed', 'TimedOut'].includes(displayStatus)) && (
            <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded text-xs text-red-700 dark:text-red-300">
              {errorMessage}
            </div>
          )}

          {bedrockJobArn && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 break-all">
              Job ARN: {bedrockJobArn}
            </div>
          )}

          {lastPolledAt && isRunning && (
            <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Last checked: {new Date(lastPolledAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BedrockEvaluationProgress;
